import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerDepartmentDetailsResource } from '@/types/resources';
import { api } from '@/utils/api';

import {
  Box,
  BoxHeadingBrake,
  BoxHeadingRow,
  Column,
  ColumnRight,
  EditButton,
} from '..';
import FreelancerDepartmentModal from './FreelancerDepartmentModal';

const DepartmentNumber = styled(Text)`
  margin-right: 8px;
  height: 24px;
  width: 48px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeparmentHeading = styled.div`
  display: flex;
  align-items: center;
`;

const Heading = styled(Text)`
  margin-bottom: 6px;
`;

export const DeleteButton = styled(Button)`
  border-color: transparent;
  font-size: 11px;
  line-height: 15px;
  height: 27px;
  padding: 0 15px;
  &:hover {
    background: ${({ theme }) => theme.colors.errorLight};
    color: ${({ theme }) => theme.colors.error};
    border-color: ${({ theme }) => theme.colors.errorLight};
  }
`;

const StyledText = styled(Text)`
  line-height: 22px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

interface Props {
  department: FreelancerDepartmentDetailsResource;
  index: number;
  departmentsCount: number;
}

const FreelancerDepartment: FC<Props> = ({
  department,
  index,
  departmentsCount,
}) => {
  const [showModal, setShowModal] = useState(false);

  const userCanSeeData = useHasPermission('view all freelancers');
  const userCanManageData = useHasPermission('manage freelancers');

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      const confirm = await alerts.confirm(
        'Are you sure you want to delete this department?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/payments/freelancers/departments/${department.id}`,
      );

      queryClient.invalidateQueries(['freelancers_details'], {
        exact: false,
      });

      await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
        exact: false,
      });

      alerts.success('Department was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <BoxHeadingRow>
        <DeparmentHeading>
          {departmentsCount > 1 && (
            <DepartmentNumber $type="smallBold">Nr. {index}</DepartmentNumber>
          )}
          <Text $color="gray" $type="smallHeadline">
            DEPARTMENT DETAILS
          </Text>
        </DeparmentHeading>
        <BoxHeadingBrake />
        {userCanManageData && (
          <Buttons>
            <EditButton
              small
              color="secondary"
              onClick={() => setShowModal(true)}
            >
              Edit
            </EditButton>
            <DeleteButton small color="secondary" onClick={onDelete}>
              Delete
            </DeleteButton>
          </Buttons>
        )}
      </BoxHeadingRow>
      <Box>
        <Column>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Companies
            </Heading>
            <StyledText $type="captionSemibold">
              {department.companies.map((c) => c.name).join(', ') || '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Status
            </Heading>
            <StyledText $type="captionSemibold">
              {department.status ?? '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Subunit
            </Heading>
            <StyledText $type="captionSemibold">
              {department.subunit?.name ?? '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Finance Account Number
            </Heading>
            <StyledText $type="captionSemibold">
              {department.financeAccountNumber?.name ?? '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Start Date
            </Heading>
            <StyledText $type="captionSemibold">
              {department.startedAt ?? '-'}
            </StyledText>
          </div>
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                Trial End Date
              </Heading>
              <StyledText $type="captionSemibold">
                {department.trialEndedAt ?? '-'}
              </StyledText>
            </div>
          )}
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                Work Format
              </Heading>
              <StyledText $type="captionSemibold">
                {department.workFormat ?? '-'}
              </StyledText>
            </div>
          )}
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                Benefits
              </Heading>
              <StyledText $type="captionSemibold">
                {department.benefits ? 'Yes' : 'No'}
              </StyledText>
            </div>
          )}
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                ORGANIMI
              </Heading>
              <StyledText $type="captionSemibold">
                {department.organimi ? 'Yes' : 'No'}
              </StyledText>
            </div>
          )}
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                Comments
              </Heading>
              <StyledText $type="captionSemibold">
                {department.comments ?? '-'}
              </StyledText>
            </div>
          )}
        </Column>
        <ColumnRight>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Department
            </Heading>
            <StyledText $type="captionSemibold">
              {department.department?.name ?? '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Role
            </Heading>
            <StyledText $type="captionSemibold">
              {department.role?.name ?? '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Team Lead
            </Heading>
            <StyledText $type="captionSemibold">
              {department.team_lead ?? '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Employment Type
            </Heading>
            <StyledText $type="captionSemibold">
              {department.employmentType ?? '-'}
            </StyledText>
          </div>
          <div>
            <Heading $type="smallHeadline" $color="gray">
              Contract Type
            </Heading>
            <StyledText $type="captionSemibold">
              {department.contractType ?? '-'}
            </StyledText>
          </div>

          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                End Date
              </Heading>
              <StyledText $type="captionSemibold">
                {department.endedAt ?? '-'}
              </StyledText>
            </div>
          )}
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                Office / Work Space
              </Heading>
              <StyledText $type="captionSemibold">
                {department.workSpace ?? '-'}
              </StyledText>
            </div>
          )}
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                Bonuses
              </Heading>
              <StyledText $type="captionSemibold">
                {department.bonuses ? 'Yes' : 'No'}
              </StyledText>
            </div>
          )}
          {userCanSeeData && (
            <div>
              <Heading $type="smallHeadline" $color="gray">
                Accident Insurance
              </Heading>
              <StyledText $type="captionSemibold">
                {department.insurance ? 'Yes' : 'No'}
              </StyledText>
            </div>
          )}
        </ColumnRight>
      </Box>
      <AnimatePresence>
        {userCanManageData && showModal && (
          <FreelancerDepartmentModal
            department={department}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancerDepartment;
